import type { ReactNode } from "react";

import { type GuideStepSummary, GuideTag, selectAssistPanels, useAssistPanelStore } from "@expert/solve-tools";
import { ActionIcon, Button, Collapse, Group, ScrollArea, Stack, Text } from "@mantine/core";
import { IconChevronDown, IconChevronUp, IconX } from "@tabler/icons-react";
import clsx from "clsx";
import { useLayoutEffect } from "react";

import headerStyles from "@/panels/modules/panels/components/GradientHeader.module.css";
import { AssistTextHighlight } from "@/timeline/AssistTextHighlight";

import classes from "./GuidedFlowsPanel.module.css";

interface GuidedFlowsPanelProps {
    guide: ReactNode;
}

export function GuidedFlowsPanel({ guide }: GuidedFlowsPanelProps) {
    return (
        <ScrollArea pos="absolute" inset={0} scrollbars="y" classNames={{ content: classes.content }}>
            <AssistTextHighlight surface="guide">{guide}</AssistTextHighlight>
        </ScrollArea>
    );
}

interface InlineGuidedFlowsPanelProps {
    sessionId: string;
    guide: ReactNode;
    title: string;
    step: GuideStepSummary | undefined;
    onClose: () => void;
}

const DRAWER_TRANSITION_MS = 200;

/**
 * A drawer docked at the top of the AI Assist tab, above the proactive-guidance card. The guide body slides down
 * from the top with its handle bar along the bottom edge, capped at half the tab. Each can be collapsed on its
 * own (both at once is fine), but the two are never expanded together: expanding the guide collapses the card,
 * and expanding the card collapses the guide. Collapsed, only the handle shows, with the step and progress. The
 * guide stays mounted while collapsed so the expert keeps their place in the step.
 */
export function InlineGuidedFlowsPanel({ sessionId, guide, title, step, onClose }: InlineGuidedFlowsPanelProps) {
    const isExpanded = useAssistPanelStore((state) => selectAssistPanels(state, sessionId).isGuideExpanded);
    const { expandPanel, collapsePanel, releaseGuide } = useAssistPanelStore.getState();

    // Layout effect so the proactive-guidance card collapses before the newly opened guide is painted.
    useLayoutEffect(() => {
        expandPanel(sessionId, "guide");
        return () => releaseGuide(sessionId);
    }, [sessionId, expandPanel, releaseGuide]);

    return (
        <Stack className={classes.inlineRoot} gap={0}>
            <Collapse
                expanded={isExpanded}
                transitionDuration={DRAWER_TRANSITION_MS}
                transitionTimingFunction="ease"
                data-testid="guide-drawer-body"
            >
                <ScrollArea.Autosize mah="50cqh" scrollbars="y" type="auto">
                    <AssistTextHighlight surface="guide">{guide}</AssistTextHighlight>
                </ScrollArea.Autosize>
            </Collapse>
            <GuideDrawerHandle
                title={title}
                step={step}
                isExpanded={isExpanded}
                onExpand={() => expandPanel(sessionId, "guide")}
                onCollapse={() => collapsePanel(sessionId, "guide")}
                onClose={onClose}
            />
        </Stack>
    );
}

interface GuideDrawerHandleProps {
    title: string;
    step: GuideStepSummary | undefined;
    isExpanded: boolean;
    onExpand: () => void;
    onCollapse: () => void;
    onClose: () => void;
}

function GuideDrawerHandle({ title, step, isExpanded, onExpand, onCollapse, onClose }: GuideDrawerHandleProps) {
    return (
        <Group
            className={clsx(headerStyles.header, classes.drawerHandle)}
            data-testid="guide-drawer-handle"
            gap="xs"
            justify="space-between"
            px="sm"
            py="xs"
            wrap="nowrap"
        >
            <Group gap="xs" wrap="nowrap" miw={0}>
                <Text fw={600} size="sm" lineClamp={1}>
                    {title}
                </Text>
                {!isExpanded && step?.label ? (
                    <Text c="dark.1" size="xs" lineClamp={1}>
                        {step.label}
                    </Text>
                ) : null}
                {!isExpanded && step ? <GuideTag>{`${step.progress}%`}</GuideTag> : null}
            </Group>
            {isExpanded ? (
                <Group gap={0} wrap="nowrap">
                    <ActionIcon
                        aria-expanded
                        aria-label="Collapse guide"
                        className={headerStyles.headerIcon}
                        onClick={onCollapse}
                        variant="transparent"
                    >
                        <IconChevronUp size={16} />
                    </ActionIcon>
                    <ActionIcon
                        aria-label="Close"
                        className={headerStyles.headerIcon}
                        onClick={onClose}
                        variant="transparent"
                    >
                        <IconX size={16} />
                    </ActionIcon>
                </Group>
            ) : (
                <Group gap="4xs" wrap="nowrap">
                    <Button color="dark.7" onClick={onExpand} radius="xl" size="compact-xs" variant="filled">
                        Open guide
                    </Button>
                    <ActionIcon
                        aria-expanded={false}
                        aria-label="Expand guide"
                        className={headerStyles.headerIcon}
                        onClick={onExpand}
                        variant="transparent"
                    >
                        <IconChevronDown size={16} />
                    </ActionIcon>
                </Group>
            )}
        </Group>
    );
}
